import {
  ChangeEvent,
  memo,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { Box, Card, CardBody, Heading, SimpleGrid } from '@chakra-ui/react';
import { debounce, isEmpty, sortBy } from 'lodash';

import { QuestionResultsModal } from '../../components/Visualization/QuestionResultsModal';
import useFilterProvider from '../../HOCs/Filter/useFilterProvider';
import useIsAdmin from '../../hooks/useIsAdmin';
import { ANALYTICS_EVENT_NAME } from '../../utils/constants/analytics-constants';
import {
  getQuestionAnalysis,
  getQuestionAnswers,
} from '../../utils/functions/display-survey.utils';
import i18n from '../../utils/i18n';

import AnalysisAndVisualizationCard from './components/AnalysisAndVisualizationCard';
import SearchAndFilterBar from './components/SearchAndFilterBar';
import {
  DATA_TABLE_VISUALIZATIONS,
  EDITABLE_VISUALIZATIONS,
} from './visualization-options-allow-list';
import { FilterType } from '.';

interface InsightsEngagementDetailsProps {
  toggleFilterDrawer: () => void;
  localFilterGroups: FilterType[];
}

const InsightsEngagementDetails = ({
  toggleFilterDrawer,
  localFilterGroups,
}: InsightsEngagementDetailsProps) => {
  const { engagementDetails, engagementResponses } = useFilterProvider();
  const isAdmin = useIsAdmin();

  const [engagementQuestions, setEngagementQuestions] = useState([
    ...engagementDetails.questions,
  ]);

  // search string
  const [searchQuery, setSearchQuery] = useState<string>('');

  const [displayPrevAvg, setDisplayPrevAvg] = useState(true);
  const togglePrevAvgDisplay = () => setDisplayPrevAvg((prev) => !prev);

  // State for toggling to DataTable view
  const [dataTablesDisplayStates, setDataTablesDisplayStates] = useState<
    Record<string, boolean>
  >({});

  // State for toggling to Edit metadata view
  const [editDisplayStates, setEditDisplayStates] = useState<
    Record<string, boolean>
  >({});

  // Toggles the displayDataTable state for a specific question by ID
  const toggleDataTableDisplay = useCallback((questionId: string) => {
    setDataTablesDisplayStates((prev) => ({
      ...prev,
      [questionId]: !prev[questionId],
    }));
  }, []);

  // Toggles the editDisplay state for a specific question by ID
  const toggleEditDisplay = useCallback((questionId: string) => {
    setEditDisplayStates((prev) => ({
      ...prev,
      [questionId]: !prev[questionId],
    }));
  }, []);

  // Resync engagementQuestions state when engagementDetails.questions changes (after edits)
  useEffect(() => {
    setEngagementQuestions([...engagementDetails.questions]);
  }, [engagementDetails.questions]);

  // Initialize both states based on their respective visualization allow-lists
  useEffect(() => {
    const dataTableInitial: Record<string, boolean> = {};
    const editInitial: Record<string, boolean> = {};

    engagementQuestions.forEach((question) => {
      const visualization = question.visualization as string;

      if (DATA_TABLE_VISUALIZATIONS.has(visualization)) {
        dataTableInitial[question.id] = false;
      }
      if (isAdmin && EDITABLE_VISUALIZATIONS.has(visualization)) {
        editInitial[question.id] = false;
      }
    });
    setDataTablesDisplayStates(dataTableInitial);
    setEditDisplayStates(editInitial);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [engagementQuestions]);

  const searchQuestions = useCallback((value: string) => {
    window.analytics.track(ANALYTICS_EVENT_NAME.ENGAGEMENT_DETAILS.SEARCH, {
      engagementId: engagementDetails.id,
      name: engagementDetails.name,
      status: engagementDetails.resultsStatus,
      searchTerm: value,
    });
    const filteredQuestions = engagementDetails.questions.filter((question) => {
      if (!value) return true;
      return (
        question.label.toLowerCase().includes(value.toLowerCase()) ||
        question.type.toLowerCase().includes(value.toLowerCase()) ||
        engagementDetails.analysis?.questionAnalyses
          .find((analysis) => analysis.questionId === question.id)
          ?.content.toLowerCase()
          .includes(value.toLowerCase())
      );
    });
    setEngagementQuestions([...filteredQuestions]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const debouncedSearchSearchQueryChange = useMemo(() => {
    return debounce(searchQuestions, 500);
  }, [searchQuestions]);

  const clearSearchQuery = () => {
    setSearchQuery('');
    searchQuestions('');
  };

  const renderQuestionAnalysisCards = useCallback(() => {
    return sortBy(engagementQuestions, 'position').map((question, index) => {
      const questionAnalysis = getQuestionAnalysis(
        engagementDetails,
        question.id
      );

      const questionAnswers = getQuestionAnswers(
        engagementResponses,
        question.id
      );

      const isQuestionAddedToFilter = localFilterGroups.some(
        (filterGroup) => filterGroup.questionId === question.id
      );

      return (
        <AnalysisAndVisualizationCard
          key={question.id || index}
          question={question}
          questionAnalysis={questionAnalysis}
          questionAnswers={questionAnswers}
          isQuestionAddedToFilter={isQuestionAddedToFilter}
          displayPrevAvg={displayPrevAvg}
          displayDataTable={dataTablesDisplayStates[question.id]}
          displayEditToggle={editDisplayStates[question.id]}
          togglePrevAvgDisplay={togglePrevAvgDisplay}
          toggleFilterDrawer={toggleFilterDrawer}
          toggleDataTableDisplay={() => toggleDataTableDisplay(question.id)}
          toggleEditDisplay={() => toggleEditDisplay(question.id)}
          useQuestionComparativeModal={false} // context of survey results page, not question comparison
        />
      );
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    engagementQuestions,
    engagementResponses,
    dataTablesDisplayStates,
    editDisplayStates,
    localFilterGroups,
  ]);

  return (
    <Box
      key={'insights-engagement-details'}
      aria-label="insights-engagement-details"
    >
      <SearchAndFilterBar
        searchQuery={searchQuery}
        handleSearchQueryChange={(event: ChangeEvent<HTMLInputElement>) => {
          setSearchQuery(event.target.value);
          debouncedSearchSearchQueryChange(event.target.value);
        }}
        clearSearchQuery={clearSearchQuery}
        toggleFilterDrawer={toggleFilterDrawer}
      />
      <SimpleGrid mt={8} columns={2} columnGap="4" rowGap="4">
        {!isEmpty(engagementQuestions) ? (
          <>{renderQuestionAnalysisCards()}</>
        ) : (
          <Card mt={4}>
            <CardBody>
              <Heading fontSize={'lg'}>
                {i18n.t('engagementDetails.questionCards.noSearchResults')}
              </Heading>
            </CardBody>
          </Card>
        )}
      </SimpleGrid>

      {/* Opened only in this context of surveys result page, if hash `#expandedq${QuestionId}` present in the URL.
      Action triggered in <Visualization> */}
      <QuestionResultsModal
        displayPrevAvg={displayPrevAvg}
        hideFilterBar={false}
        togglePrevAvgDisplay={togglePrevAvgDisplay}
        dataTablesDisplayStates={dataTablesDisplayStates}
        toggleDataTableDisplay={toggleDataTableDisplay}
        editDisplayStates={editDisplayStates}
        toggleEditDisplay={toggleEditDisplay}
      />
    </Box>
  );
};

export default memo(InsightsEngagementDetails);
